import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getSDK } from '@/store/sdkStore';
import { createLogger } from '@/lib/logger';
import { logSwallowed } from '@/lib/errors';
import { formatValue } from '@/lib/format';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { visibilityAllows } from '@/lib/site/visibility';
import { useFileImage } from '@/lib/widgets/fileImage';
import { SiteListFilters, type SiteListFieldMeta } from './SiteListFilters';
import {
  buildSiteRecordFilters,
  parseStaticFilters,
  readSiteListState,
  siteListCsv,
  siteListParamsKey,
  SITE_LIST_RESERVED,
} from '@/lib/site/siteListParams';
import type { BlockAppearance } from '@/lib/widgets/blocks';

const log = createLogger('SiteList');

// ============================================================================
// SiteListBlock — ПРИМИТИВ `site_list` ДВИЖКА РЕНДЕРА (SSoT/SRP, см.
// plans/plan-render-engine.md): публичный список записей для САЙТОВ.
// Данные — сервер (SiteService.ListSiteRecords: таблица/схема/поля/права
// «Гостя»); клиент НЕ знает schema. Отборы/поиск/сортировка/страница уходят
// на СЕРВЕР (фильтрует он), состояние списка живёт в query-строке
// (lib/site/siteListParams) — ссылка/назад/перезагрузка сохраняют вид.
// Оформление — токенами сайта (--site-*), НЕ учётными стилями.
// Пресеты: cards | rows | tiles.
// ============================================================================

const SYSTEM_FIELDS = new Set([
  'created_at', 'updated_at', 'created_by', 'updated_by',
  'is_marked', 'is_group', 'parent_id', 'owner_id',
]);

interface RecordRow {
  id: string;
  values: Record<string, string>;
  presentations: Record<string, string>;
}

interface Props {
  objectType?: string;
  objectCode?: string;
  preset?: string;
  /** Вариант из конструктора (variants в схеме блока) — равен preset. */
  variant?: string;
  columns?: string;
  titleField?: string;
  imageField?: string;
  itemUrl?: string;
  limit?: number | string;
  gap?: number | string;
  radius?: number | string;
  background?: string;
  padding?: number | string;
  hover?: boolean | string;
  showTitle?: boolean;
  columnsCount?: number | string;
  // Отборы/поиск/сортировка/страницы (без изменения proto — поля ListSiteRecordsRequest).
  filters?: unknown;
  filterFields?: string;
  searchField?: string;
  searchPlaceholder?: string;
  sort?: string;
  sortDesc?: boolean | string;
  sortFields?: string;
  showPager?: boolean;
  emptyText?: string;
  appearance?: BlockAppearance;
}

const currentSiteCode = (): string => {
  const m = window.location.pathname.match(/^\/p\/([^/]+)/);
  return m ? decodeURIComponent(m[1]) : '';
};

const num = (v: unknown, def: number): number => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : def;
};

/** CardImage — превью файла-изображения записи (id файла → data URL). */
function CardImage({ fileId, radius }: { fileId: string; radius: number }) {
  const { src, error } = useFileImage(fileId);
  if (error) return null;
  if (!src) {
    return (
      <div
        style={{
          height: 140, borderRadius: radius, marginBottom: 12,
          background: 'color-mix(in oklab, var(--site-surface-alt) 70%, transparent)',
        }}
      />
    );
  }
  return (
    <img
      src={src}
      alt=""
      style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: radius, marginBottom: 12, display: 'block' }}
    />
  );
}

export function SiteListBlock({ props }: { props: Props }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile } = useBreakpoint();

  const site = currentSiteCode();
  const objectType = String(props.objectType || '').trim();
  const objectCode = String(props.objectCode || '').trim();
  const searchField = String(props.searchField || '').trim();

  const filterFieldList = useMemo(
    () => siteListCsv(props.filterFields).filter((c) => !SITE_LIST_RESERVED.has(c)),
    [props.filterFields],
  );
  const sortFieldList = useMemo(() => siteListCsv(props.sortFields), [props.sortFields]);
  const staticFilters = useMemo(() => parseStaticFilters(props.filters), [props.filters]);
  const state = useMemo(() => readSiteListState(searchParams, filterFieldList), [searchParams, filterFieldList]);

  const pageSize = num(props.limit, 12);
  const effectiveSort = state.sort || String(props.sort || '').trim();
  const effectiveSortDesc = state.sort
    ? state.sortDesc
    : String(props.sortDesc) === 'true' || props.sortDesc === true;

  const serverFilters = useMemo(
    () => buildSiteRecordFilters(staticFilters, state, searchField),
    [staticFilters, state, searchField],
  );
  const filtersKey = siteListParamsKey(serverFilters);

  const [fields, setFields] = useState<SiteListFieldMeta[]>([]);
  const [rows, setRows] = useState<RecordRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [enumOptions, setEnumOptions] = useState<Record<string, Array<{ value: string; label: string }>>>({});
  const [hoverId, setHoverId] = useState('');
  const loadedRef = useRef(false);

  const appearance = props.appearance || {};
  const hidden = !visibilityAllows(
    String(appearance.visibility || ''),
    String(appearance.visibilityRole || ''),
    isMobile,
  );
  const bgImageId = appearance.bg === 'image' && appearance.bgImage ? String(appearance.bgImage) : '';
  const { src: bgImageSrc } = useFileImage(bgImageId);

  // Данные — СЕРВЕР (фильтры/поиск/сортировка/страница уходят в запрос).
  useEffect(() => {
    if (!site || !objectType || !objectCode) {
      setError('site_list: укажите тип и объект');
      setLoading(false);
      return;
    }
    let cancelled = false;
    if (!loadedRef.current) setLoading(true);
    (async () => {
      try {
        const res: any = await getSDK().site.listSiteRecords({
          site,
          objectType,
          objectCode,
          filters: serverFilters,
          page: state.page - 1,
          pageSize,
          sort: effectiveSort,
          sortDesc: effectiveSortDesc,
        });
        if (cancelled) return;
        setFields((res?.fields || []) as SiteListFieldMeta[]);
        setRows((res?.records || []) as RecordRow[]);
        setTotal(Number(res?.total || 0));
        setError('');
      } catch (e) {
        if (cancelled) return;
        log.error('ListSiteRecords', { error: String(e) });
        setError('Не удалось загрузить записи: ' + String(e));
      } finally {
        if (!cancelled) { loadedRef.current = true; setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site, objectType, objectCode, filtersKey, state.page, pageSize, effectiveSort, effectiveSortDesc]);

  // Страница вне диапазона (данных стало меньше) — детерминированно возвращаем
  // на последнюю страницу, а не показываем пустоту.
  useEffect(() => {
    if (loading || error || total <= 0) return;
    const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
    if (state.page <= totalPages) return;
    const next = new URLSearchParams(searchParams);
    if (totalPages <= 1) next.delete('page'); else next.set('page', String(totalPages));
    setSearchParams(next, { replace: true });
  }, [loading, error, total, pageSize, state.page, searchParams, setSearchParams]);

  // Значения перечислений для отборов (публичный ListEnums — без входа).
  useEffect(() => {
    const codes = Array.from(new Set(
      fields
        .filter((f) => f.dataType.toLowerCase() === 'enumeration' && f.refTable && filterFieldList.includes(f.column))
        .map((f) => f.refTable),
    ));
    if (codes.length === 0) return;
    let cancelled = false;
    getSDK().metadata.listEnums({})
      .then((r: any) => {
        if (cancelled) return;
        const map: Record<string, Array<{ value: string; label: string }>> = {};
        for (const code of codes) {
          const en = ((r?.items || []) as any[]).find((e: any) => e.code === code);
          map[code] = ((en?.values || []) as any[])
            .map((v: any) => ({ value: String(v.value ?? ''), label: String(v.synonym || v.name || v.value || '') }))
            .filter((o) => o.value !== '');
        }
        setEnumOptions(map);
      })
      .catch((e) => logSwallowed(e, 'components/site/SiteListBlock.tsx:enumOptions'));
    return () => { cancelled = true; };
  }, [fields, filterFieldList]);

  const updateParams = useCallback((mutate: (p: URLSearchParams) => void) => {
    const next = new URLSearchParams(searchParams);
    mutate(next);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const onSearch = useCallback((q: string) => {
    updateParams((p) => { if (q) p.set('q', q); else p.delete('q'); p.delete('page'); });
  }, [updateParams]);
  const onField = useCallback((field: string, value: string) => {
    updateParams((p) => { if (value) p.set(field, value); else p.delete(field); p.delete('page'); });
  }, [updateParams]);
  const onSort = useCallback((sort: string, desc: boolean) => {
    updateParams((p) => {
      if (sort) p.set('sort', sort); else p.delete('sort');
      if (sort && desc) p.set('dir', 'desc'); else p.delete('dir');
      p.delete('page');
    });
  }, [updateParams]);
  const onReset = useCallback(() => {
    updateParams((p) => {
      for (const k of ['q', 'page', 'sort', 'dir']) p.delete(k);
      for (const f of filterFieldList) p.delete(f);
    });
  }, [updateParams, filterFieldList]);
  const goPage = useCallback((page: number) => {
    updateParams((p) => { if (page <= 1) p.delete('page'); else p.set('page', String(page)); });
  }, [updateParams]);

  if (hidden) return null;

  const preset = String(props.preset || props.variant || 'cards');
  const titleField = String(props.titleField || 'name');
  const imageField = String(props.imageField || '').trim();
  const cols = siteListCsv(props.columns);
  const fieldMap = new Map(fields.map((f) => [f.column, f]));
  const metaCols = (cols.length ? cols : fields.map((f) => f.column))
    .filter((c) => c !== titleField && c !== imageField && !SYSTEM_FIELDS.has(c) && fieldMap.has(c))
    .slice(0, 4);

  const value = (r: RecordRow, col: string): string => {
    const f = fieldMap.get(col);
    const raw = f?.isRef ? (r.presentations[col] || '') : (r.values[col] || '');
    if (!raw) return '';
    return formatValue(raw, f?.format || '', f?.dataType || '');
  };
  const title = (r: RecordRow) => value(r, titleField) || r.values.title || r.values.name || r.id.slice(0, 8);
  const href = (r: RecordRow): string => {
    if (!props.itemUrl) return '';
    const ref = r.values.code || r.values.number || r.id;
    return String(props.itemUrl)
      .replace(/\{ref\}/g, encodeURIComponent(ref))
      .replace(/\{site\}/g, currentSiteCode());
  };

  const gap = num(props.gap, preset === 'rows' ? 0 : 20);
  const radius = num(props.radius, 12);
  const padding = num(props.padding, 20);
  const titleStyle: React.CSSProperties = {
    fontFamily: 'var(--site-font-heading)',
    fontSize: 20, fontWeight: 600, lineHeight: 1.25,
    letterSpacing: '-0.01em', margin: 0, color: 'var(--site-text)',
  };
  const metaStyle: React.CSSProperties = { color: 'var(--site-muted)', fontSize: 14, lineHeight: 1.5 };

  const maxWidth = appearance.maxWidth && appearance.maxWidth > 0
    ? `${appearance.maxWidth}px`
    : 'var(--site-max-width)';
  const wrap: React.CSSProperties = { maxWidth, margin: '0 auto', padding: '0 24px' };

  const sectionStyle: React.CSSProperties = {};
  if (appearance.bg === 'color' && appearance.bgColor) sectionStyle.background = appearance.bgColor;
  if (appearance.bg === 'gradient' && appearance.bgColor) {
    sectionStyle.background = `linear-gradient(135deg, ${appearance.bgColor}, ${appearance.bgColorTo || appearance.bgColor})`;
  }
  if (appearance.bg === 'image' && bgImageSrc) {
    const o = Math.min(90, Math.max(0, Number(appearance.overlay) || 0)) / 100;
    sectionStyle.backgroundImage = `linear-gradient(rgba(0,0,0,${o}), rgba(0,0,0,${o})), url(${bgImageSrc})`;
    sectionStyle.backgroundSize = 'cover';
    sectionStyle.backgroundPosition = 'center';
  }
  if (appearance.paddingY) {
    sectionStyle.paddingTop = appearance.paddingY;
    sectionStyle.paddingBottom = appearance.paddingY;
  }

  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  // Пагинация — ЯВНОЕ включение (по умолчанию выключена: у существующих сайтов
  // `limit` исторически означает «показать N», вид не меняется без настройки).
  const showPager = (props.showPager === true || String(props.showPager) === 'true') && totalPages > 1;
  const emptyText = String(props.emptyText || 'Записей нет');

  const cardBody = (r: RecordRow) => (
    <>
      <div style={titleStyle}>{title(r)}</div>
      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {metaCols.map((c) => {
          const v = value(r, c);
          if (!v) return null;
          return <div key={c} style={metaStyle}>{v}</div>;
        })}
      </div>
      {props.itemUrl ? (
        <div style={{ marginTop: 14, color: 'var(--site-primary)', fontSize: 14, fontWeight: 500 }}>Подробнее ↗</div>
      ) : null}
    </>
  );

  const pager = showPager ? (
    <div style={{ ...wrap, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginTop: 20, fontSize: 14 }}>
      <button
        type="button"
        disabled={state.page <= 1}
        onClick={() => goPage(state.page - 1)}
        style={{
          ...metaStyle, cursor: state.page <= 1 ? 'default' : 'pointer', opacity: state.page <= 1 ? 0.4 : 1,
          border: '1px solid var(--site-border-color)', borderRadius: 8, padding: '6px 14px',
          background: 'transparent', color: 'var(--site-text)',
        }}
      >
        ← Назад
      </button>
      <span style={metaStyle}>Стр. {state.page} из {totalPages} · всего {total}</span>
      <button
        type="button"
        disabled={state.page >= totalPages}
        onClick={() => goPage(state.page + 1)}
        style={{
          ...metaStyle, cursor: state.page >= totalPages ? 'default' : 'pointer', opacity: state.page >= totalPages ? 0.4 : 1,
          border: '1px solid var(--site-border-color)', borderRadius: 8, padding: '6px 14px',
          background: 'transparent', color: 'var(--site-text)',
        }}
      >
        Вперёд →
      </button>
    </div>
  ) : null;

  const body = (
    <>
      <SiteListFilters
        fields={fields}
        filterFields={filterFieldList}
        searchField={searchField}
        searchPlaceholder={String(props.searchPlaceholder || '')}
        sortFields={sortFieldList}
        maxWidth={maxWidth}
        enumOptions={enumOptions}
        state={state}
        onSearch={onSearch}
        onField={onField}
        onSort={onSort}
        onReset={onReset}
      />

      {error ? (
        <div style={{ ...wrap, color: 'var(--site-muted)', padding: 16 }}>{error}</div>
      ) : loading ? (
        <div style={{ ...wrap, ...metaStyle, padding: 16 }}>Загрузка…</div>
      ) : rows.length === 0 ? (
        <div style={{ ...wrap, ...metaStyle, padding: 16, textAlign: 'center' }}>{emptyText}</div>
      ) : preset === 'rows' ? (
        <div style={wrap}>
          {rows.map((r) => {
            const h = href(r);
            const inner = (
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16 }}>
                <div style={titleStyle}>{title(r)}</div>
                <div style={{ ...metaStyle, whiteSpace: 'nowrap' }}>
                  {metaCols.map((c) => value(r, c)).filter(Boolean).join(' · ')}
                </div>
              </div>
            );
            return (
              <div
                key={r.id}
                style={{
                  padding: '16px 8px', borderBottom: '1px solid var(--site-border-color)',
                  background: hoverId === r.id ? 'color-mix(in oklab, var(--site-surface) 60%, transparent)' : 'transparent',
                  transition: 'background .15s ease',
                }}
                onMouseEnter={() => setHoverId(r.id)}
                onMouseLeave={() => setHoverId('')}
              >
                {h ? <a href={h} style={{ textDecoration: 'none' }}>{inner}</a> : inner}
              </div>
            );
          })}
        </div>
      ) : (
        (() => {
          const min = preset === 'tiles' ? 180 : 260;
          const fixedCols = num(props.columnsCount, 0);
          const gridTemplate = fixedCols > 0
            ? `repeat(${Math.min(fixedCols, 6)}, 1fr)`
            : `repeat(auto-fill, minmax(${min}px, 1fr))`;
          return (
            <div style={{ ...wrap, display: 'grid', gridTemplateColumns: gridTemplate, gap }}>
              {rows.map((r) => {
                const h = href(r);
                const imageId = imageField ? (r.values[imageField] || '') : '';
                const cardBodyEl = (
                  <div
                    style={{
                      background: props.background
                        || 'linear-gradient(145deg, var(--site-surface), var(--site-surface-alt))',
                      border: '1px solid var(--site-border-color)',
                      borderRadius: radius,
                      padding,
                      minHeight: preset === 'tiles' ? 120 : 170,
                      height: '100%',
                      transition: 'transform .18s ease, border-color .18s ease, box-shadow .18s ease',
                      transform: hoverId === r.id ? 'translateY(-6px)' : 'none',
                      borderColor: hoverId === r.id
                        ? 'color-mix(in oklab, var(--site-primary) 45%, var(--site-border-color))'
                        : 'var(--site-border-color)',
                      boxShadow: hoverId === r.id ? '0 18px 40px rgba(0,0,0,.35)' : 'none',
                    }}
                    onMouseEnter={() => setHoverId(r.id)}
                    onMouseLeave={() => setHoverId('')}
                  >
                    {imageId ? <CardImage fileId={imageId} radius={Math.min(radius, 10)} /> : null}
                    {cardBody(r)}
                  </div>
                );
                return h
                  ? <a key={r.id} href={h} style={{ textDecoration: 'none', color: 'inherit' }}>{cardBodyEl}</a>
                  : <div key={r.id}>{cardBodyEl}</div>;
              })}
            </div>
          );
        })()
      )}

      {pager}
    </>
  );

  return (
    <section style={sectionStyle} className={`w-full ${appearance.className || ''}`}>
      {body}
    </section>
  );
}
